import { CrossIcon } from "@components/Icons/Cross";
import { EditIcon } from "@components/Icons/Edit";
import { Input } from "@components/Input";
import { FC } from "react";
import { useTranslation } from "@helpers/useTranslation";

type Props = {
  scenarioIndex: number;
  errors: any;
  register: any;
  setActiveScenarioToRemove?: (index: number) => void;
  moveScenario?: (index: number, direction: "up" | "down") => void;
  isLast?: boolean;
  isFirst?: boolean;
  scenario?: BettingScenario;
  onResolveOutcome?: (scenarioIndex: number, outcome: number) => void;
  showResolveButton?: boolean;
  onOpenResolveModal?: (scenarioIndex: number) => void;
};

export const BettingScenario: FC<Props> = ({
  scenarioIndex,
  errors,
  register,
  setActiveScenarioToRemove,
  moveScenario,
  isLast,
  isFirst,
  scenario,
  onResolveOutcome: _onResolveOutcome,
  showResolveButton = false,
  onOpenResolveModal,
}) => {
  const { t } = useTranslation("admin");
  return (
    <>
      <div className="mb-8">
        <div className="mb-4 flex flex-row">
          <Input
            label={`${t("scenario")} ${scenarioIndex + 1}`}
            name={`scenarios.${scenarioIndex}.scenario`}
            required
            requiredMessage={t("fieldRequired")}
            errors={errors}
            register={register}
            renderTopRight={() => (
              <div className="font-bold text-neutral">
                <button
                  type="button"
                  disabled={isFirst}
                  className={`mx-2 ${isFirst ? "text-gray-300" : ""}`}
                  onClick={() => moveScenario(scenarioIndex, "up")}
                >
                  ↑
                </button>
                <button
                  type="button"
                  disabled={isLast}
                  className={`mx-2 ${isLast ? "text-gray-300" : ""}`}
                  onClick={() => moveScenario(scenarioIndex, "down")}
                >
                  ↓
                </button>
              </div>
            )}
          />
          <button
            className="btn-outline btn-error btn-xs btn-circle btn ml-4 mt-12"
            type="button"
            onClick={() => setActiveScenarioToRemove(scenarioIndex)}
          >
            <CrossIcon className="h-4 w-4" strokeWidth={2} />
          </button>
        </div>
        <div className="mb-4 flex flex-row justify-between">
          <span className="mr-2 text-sm">{t("option")}</span>
        </div>
        <div className="mb-2 flex flex-row">
          <Input
            placeholder={`${t("option")} 1`}
            name={`scenarios.${scenarioIndex}.option0`}
            required
            requiredMessage={t("fieldRequired")}
            errors={errors}
            register={register}
          />
        </div>
        <div className="mb-2 flex flex-row">
          <Input
            placeholder={`${t("option")} 2`}
            name={`scenarios.${scenarioIndex}.option1`}
            required
            requiredMessage={t("fieldRequired")}
            errors={errors}
            register={register}
          />
        </div>
        <div className="mb-2 flex flex-row">
          <Input
            placeholder={`${t("option")} 3 (${t("optional")})`}
            name={`scenarios.${scenarioIndex}.option2`}
            errors={errors}
            register={register}
          />
        </div>
        <div className="mb-2 flex flex-row">
          <Input
            placeholder={`${t("option")} 4 (${t("optional")})`}
            name={`scenarios.${scenarioIndex}.option3`}
            errors={errors}
            register={register}
          />
        </div>

        {showResolveButton && (
          <div className="mt-4 pb-4">
            {scenario?.correctOutcome !== undefined ? (
              <div className="flex items-center justify-between">
                <div className="rounded-lg border border-success bg-success bg-opacity-10 py-2 px-3 w-full">
                  <p className="text-sm font-medium text-green-800">
                    <strong>{t("outcomeResolved")}:</strong> {(() => {
                      const optionKey = `option${scenario.correctOutcome}` as keyof BettingScenario;
                      const optionText = scenario?.[optionKey] as string;
                      return `${optionText}`;
                    })()}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => onOpenResolveModal?.(scenarioIndex)}
                  className="btn-ghost btn-xs text-slate-500 btn p-1 hover:bg-success hover:bg-opacity-20 ml-4 p-1"
                  title={t("editOutcome")}
                >
                  <EditIcon className="h-4 w-4 text-success" />
                </button>
              </div>
            ) : (
              <div className="flex items-center justify-center w-full">
                <button
                  type="button"
                  onClick={() => onOpenResolveModal?.(scenarioIndex)}
                  className="btn-outline btn-primary btn-sm btn opacity-80"
                >
                  {t("resolveBets")}
                </button>
              </div>
            )}
          </div>
        )}

        <span className="mt-2" />
      </div>
    </>
  );
};
